import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Settings,

  Save,
  Loader2,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle,
  FolderOpen,
  Upload,
  X,
  Image as ImageIcon,
  Search,
  Pencil,
  Trash2,
  Ban,
  Plus,
  Eye,
  EyeOff,
} from 'lucide-react';
import { projectsAPI, projectConfigAPI, uploadAPI } from '../services/api';
import Modal from '../components/Modal';
import { BlockLoader } from '../components/loading';
import ProjectThemeEditor from '../components/ProjectThemeEditor';
import ProjectMobilePreview from '../components/ProjectMobilePreview';
import { HiringSeeSwitchCard } from '../components/hiring/HiringSeeSwitchCard';
import { useAuthStore } from '../stores/authStore';

const AZURE_STORAGE_FOLDER = (import.meta.env.VITE_AZURE_STORAGE_FOLDER || import.meta.env.AZURE_STORAGE_FOLDER || 'DEV').trim();
const PROJECT_SPEC_FOLDER_PREFIX = `${AZURE_STORAGE_FOLDER}/project-specification`;
const PROJECT_LOGO_UPLOAD_FOLDER = `${PROJECT_SPEC_FOLDER_PREFIX}/logos`;
const PROJECT_FAVICON_UPLOAD_FOLDER = `${PROJECT_SPEC_FOLDER_PREFIX}/favicons`;
const PROJECT_NOTIFICATION_ICON_UPLOAD_FOLDER = `${PROJECT_SPEC_FOLDER_PREFIX}/notification-icons`;

// Platform defaults — applied when a project hasn't picked its own values yet.
const DEFAULT_PRIMARY = '#1B3A54';
const DEFAULT_SECONDARY = '#264E6E';
const DEFAULT_TERTIARY = '#3B82F6';
const DEFAULT_ON_PRIMARY = '#FFFFFF';
const DEFAULT_SURFACE_ACCENT = '#E8EEF3';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Budget {
  budgetId: number;
  budgetName: string;
  jobName: string;
  employeeCategory: string;
}

interface ProjectConfig {
  id?: number;
  project_id: number;
  project_name: string | null;
  display_title: string | null;
  theme_primary_color: string;
  theme_secondary_color: string;
  theme_tertiary_color: string;
  theme_on_primary_color: string;
  theme_surface_accent_color: string;
  logo_url: string | null;
  favicon_url: string | null;
  notification_icon_url: string | null;
  description: string | null;
  is_active: boolean;
}

interface AvailableProject {
  projectId: number;
  projectName: string;
  projectCode: string;
}

// Resolve a stored path to a displayable image URL.
// Prefer the authenticated proxy (/api/images/view/*) when a blobPath exists —
// the raw Azure URL returned by the upload API 403s when the container is private.
const resolveImageUrl = (previewUrl: string | null, storedPath: string | null): string | null => {
  if (storedPath && storedPath.trim() !== '') {
    if (/^https?:\/\//i.test(storedPath)) return storedPath;
    if (storedPath.startsWith('/api/')) return storedPath;
    return `/api/images/view/${encodeURI(storedPath.replace(/^\/+/, ''))}`;
  }
  return previewUrl ?? null;
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function ProjectManagementPage() {
  const { refreshProjectList, user } = useAuthStore();
  const isSuperAdmin = user?.adminRole === 'super_admin';

  // ─── View state: 'list' or 'form' ─────────────────────────────────────────
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);

  // ─── List view state ───────────────────────────────────────────────────────
  const [configuredProjects, setConfiguredProjects] = useState<ProjectConfig[]>([]);
  const [availableProjects, setAvailableProjects] = useState<AvailableProject[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listSearch, setListSearch] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);

  // ─── Form view state ──────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'settings' | 'budgets'>('settings');
  const [projectOwner, setProjectOwner] = useState<{ fullName: string | null; employeeCode: string | null } | null>(null);

  // Project config form
  const [configForm, setConfigForm] = useState<ProjectConfig>({
    project_id: 0, project_name: '', display_title: '',
    theme_primary_color: DEFAULT_PRIMARY,
    theme_secondary_color: DEFAULT_SECONDARY,
    theme_tertiary_color: DEFAULT_TERTIARY,
    theme_on_primary_color: DEFAULT_ON_PRIMARY,
    theme_surface_accent_color: DEFAULT_SURFACE_ACCENT,
    logo_url: '', favicon_url: '', notification_icon_url: '',
    description: '', is_active: true,
  });
  const [configExists, setConfigExists] = useState(false);
  const [saving, setSaving] = useState(false);

  // File upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingNotificationIcon, setUploadingNotificationIcon] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState<string | null>(null);
  const [notificationIconPreviewUrl, setNotificationIconPreviewUrl] = useState<string | null>(null);

  // Budget state
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [budgetAccessMap, setBudgetAccessMap] = useState<Record<number, string>>({});
  // Which budgets are store-deployable field roles — drives the Onboarding
  // new-joiner worklist (iPro/Senior iPro shown, Assistant Manager hidden).
  const [budgetStoreLevelMap, setBudgetStoreLevelMap] = useState<Record<number, boolean>>({});
  const [budgetAccessExists, setBudgetAccessExists] = useState(false);
  const [savingBudgetAccess, setSavingBudgetAccess] = useState(false);
  const [budgetAccessSearch, setBudgetAccessSearch] = useState('');

  // Add project dropdown state
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [addProjectSearch, setAddProjectSearch] = useState('');

  const filteredBudgetSettings = useMemo(
    () => budgetAccessSearch.trim()
      ? budgets.filter((b) =>
          String(b.budgetName ?? '').toLowerCase().includes(budgetAccessSearch.toLowerCase()) ||
          (b.jobName && String(b.jobName).toLowerCase().includes(budgetAccessSearch.toLowerCase())))
      : budgets,
    [budgets, budgetAccessSearch]
  );

  // ─── Configured project IDs for quick lookup ──────────────────────────────
  const configuredProjectIds = useMemo(
    () => new Set(configuredProjects.map((p) => p.project_id)),
    [configuredProjects]
  );

  // Unconfigured projects for the Add dropdown
  const unconfiguredProjects = useMemo(
    () => availableProjects.filter((p) => !configuredProjectIds.has(p.projectId)),
    [availableProjects, configuredProjectIds]
  );

  const filteredUnconfigured = useMemo(
    () => addProjectSearch.trim()
      ? unconfiguredProjects.filter((p) =>
          p.projectName.toLowerCase().includes(addProjectSearch.toLowerCase()) ||
          p.projectCode.toLowerCase().includes(addProjectSearch.toLowerCase()))
      : unconfiguredProjects,
    [unconfiguredProjects, addProjectSearch]
  );

  // Filtered configured projects for search
  const filteredConfigured = useMemo(
    () => listSearch.trim()
      ? configuredProjects.filter((p) =>
          (p.project_name || '').toLowerCase().includes(listSearch.toLowerCase()) ||
          (p.display_title || '').toLowerCase().includes(listSearch.toLowerCase()) ||
          String(p.project_id).includes(listSearch))
      : configuredProjects,
    [configuredProjects, listSearch]
  );

  // ─── Fetch list data ──────────────────────────────────────────────────────
  const fetchConfiguredProjects = useCallback(async () => {
    try {
      setListLoading(true);
      const response = await projectConfigAPI.getAll();
      const data = response.data?.data || response.data || [];
      setConfiguredProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch configured projects:', err);
      setConfiguredProjects([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const fetchAvailableProjects = useCallback(async () => {
    try {
      const response = await projectsAPI.getActiveProjects();
      const raw = Array.isArray(response.data) ? response.data : response.data?.data || response.data?.projectList || [];
      const list = (Array.isArray(raw) ? raw : []).map((p: any) => ({
        projectId: p.projectId ?? p.project_id ?? p.id,
        projectName: String(p.projectName ?? p.project_name ?? p.name ?? ''),
        projectCode: String(p.projectCode ?? p.project_code ?? p.code ?? ''),
      }));
      setAvailableProjects(list);
    } catch (err) {
      console.error('Failed to fetch available projects:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfiguredProjects();
    fetchAvailableProjects();
  }, [fetchConfiguredProjects, fetchAvailableProjects]);

  // ─── Fetch data for a specific project (form view) ────────────────────────
  const fetchProjectConfig = useCallback(async (projectId: number) => {
    try {
      setLoading(true);
      const response = await projectConfigAPI.getByProject(projectId);
      const config = response.data?.data;
      if (config) {
        setConfigExists(true);
        setConfigForm({
          project_id: projectId,
          project_name: config.project_name || '',
          display_title: config.display_title || '',
          theme_primary_color: config.theme_primary_color || DEFAULT_PRIMARY,
          theme_secondary_color: config.theme_secondary_color || DEFAULT_SECONDARY,
          theme_tertiary_color: (config as any).theme_tertiary_color || DEFAULT_TERTIARY,
          theme_on_primary_color: (config as any).theme_on_primary_color || DEFAULT_ON_PRIMARY,
          theme_surface_accent_color: (config as any).theme_surface_accent_color || DEFAULT_SURFACE_ACCENT,
          logo_url: config.logo_url || '',
          favicon_url: config.favicon_url || '',
          notification_icon_url: (config as any).notification_icon_url || '',
          description: config.description || '',
          is_active: config.is_active !== false,
        });
        const rp = (val: string | null, setter: (u: string | null) => void) => {
          if (!val) { setter(null); return; }
          if (val.startsWith('http://') || val.startsWith('https://')) { setter(val); return; }
          setter(`/api/images/view/${encodeURI(val.replace(/^\/+/, ''))}`);
        };
        rp(config.logo_url, setLogoPreviewUrl);
        rp(config.favicon_url, setFaviconPreviewUrl);
        rp((config as any).notification_icon_url, setNotificationIconPreviewUrl);
      } else {
        setConfigExists(false);
        const matched = availableProjects.find((p) => p.projectId === projectId);
        setConfigForm({
          project_id: projectId,
          project_name: matched?.projectName || '', display_title: '',
          theme_primary_color: DEFAULT_PRIMARY,
          theme_secondary_color: DEFAULT_SECONDARY,
          theme_tertiary_color: DEFAULT_TERTIARY,
          theme_on_primary_color: DEFAULT_ON_PRIMARY,
          theme_surface_accent_color: DEFAULT_SURFACE_ACCENT,
          logo_url: '', favicon_url: '', notification_icon_url: '',
          description: '', is_active: true,
        });
        setLogoPreviewUrl(null);
        setFaviconPreviewUrl(null);
        setNotificationIconPreviewUrl(null);
      }
    } catch (err) {
      console.error('Failed to fetch project config:', err);
    } finally {
      setLoading(false);
    }
  }, [availableProjects]);

  const fetchBudgets = useCallback(async (projectId: number) => {
    try {
      const response = await projectsAPI.getActiveBudgets(projectId);
      setBudgets(response.data?.data || []);
    } catch (err) { console.error('Failed to fetch budgets:', err); setBudgets([]); }
  }, []);

  const fetchBudgetAccess = useCallback(async (projectId: number) => {
    try {
      const response = await projectConfigAPI.getBudgetAccess(projectId);
      const rows: { budget_id: number; application_access: string; is_store_level?: boolean }[] = response.data?.data || [];
      const map: Record<number, string> = {};
      const storeMap: Record<number, boolean> = {};
      rows.forEach((row) => {
        map[row.budget_id] = row.application_access;
        storeMap[row.budget_id] = row.is_store_level === true;
      });
      setBudgetAccessMap(map);
      setBudgetStoreLevelMap(storeMap);
      setBudgetAccessExists(rows.length > 0);
    } catch (err) { console.error('Failed to fetch budget access:', err); }
  }, []);

  // ─── Open form for a project ──────────────────────────────────────────────
  const openProjectForm = (projectId: number) => {
    setEditingProjectId(projectId);
    setView('form');
    setError(null);
    setSuccessMsg(null);
    setBudgetAccessSearch('');
    setBudgetAccessMap({});
    setBudgetAccessExists(false);
    setActiveTab('settings');
    setProjectOwner(null);
    fetchProjectConfig(projectId);
    fetchBudgets(projectId);
    fetchBudgetAccess(projectId);
    // Fetch project owner
    projectsAPI.getProjectOwner(projectId)
      .then((res) => {
        if (res.data?.data) setProjectOwner(res.data.data);
      })
      .catch(() => setProjectOwner(null));
  };

  const handleBackToList = () => {
    setView('list');
    setEditingProjectId(null);
    setError(null);
    setSuccessMsg(null);
    setBudgetAccessMap({});
    setBudgetAccessExists(false);
    setActiveTab('settings');
  };

  // ─── Toggle active status from list ────────────────────────────────────────
  const handleToggleActive = async (config: ProjectConfig) => {
    const next = !config.is_active;
    setConfiguredProjects((prev) => prev.map((p) =>
      p.project_id === config.project_id ? { ...p, is_active: next } : p
    ));
    try {
      await projectConfigAPI.upsert({
        project_id: config.project_id,
        project_name: config.project_name || undefined,
        display_title: config.display_title || undefined,
        theme_primary_color: config.theme_primary_color || undefined,
        theme_secondary_color: config.theme_secondary_color || undefined,
        theme_tertiary_color: config.theme_tertiary_color || undefined,
        theme_on_primary_color: config.theme_on_primary_color || undefined,
        theme_surface_accent_color: config.theme_surface_accent_color || undefined,
        logo_url: config.logo_url || undefined,
        favicon_url: config.favicon_url || undefined,
        notification_icon_url: config.notification_icon_url || undefined,
        description: config.description || undefined,
        is_active: next,
      });
    } catch (err: any) {
      setConfiguredProjects((prev) => prev.map((p) =>
        p.project_id === config.project_id ? { ...p, is_active: config.is_active } : p
      ));
      setError(err?.response?.data?.message || 'Failed to update project status');
      setTimeout(() => setError(null), 3000);
    }
  };

  // ─── Delete / Deactivate ──────────────────────────────────────────────────
  const handleDeleteProject = async (projectId: number) => {
    if (!window.confirm('Are you sure you want to delete this project configuration? This will remove all settings for this project.')) return;
    setDeleting(projectId);
    try {
      await projectConfigAPI.delete(projectId);
      setConfiguredProjects((prev) => prev.filter((p) => p.project_id !== projectId));
      refreshProjectList();
      setSuccessMsg('Project configuration deleted successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete project config:', err);
      setError(err.response?.data?.message || 'Failed to delete project configuration');
      setTimeout(() => setError(null), 3000);
    } finally { setDeleting(null); }
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleSaveConfig = async () => {
    if (!editingProjectId) return;
    setSaving(true); setError(null); setSuccessMsg(null);
    try {
      await projectConfigAPI.upsert({
        project_id: editingProjectId,
        project_name: configForm.project_name || undefined,
        display_title: configForm.display_title || undefined,
        theme_primary_color: configForm.theme_primary_color || undefined,
        theme_secondary_color: configForm.theme_secondary_color || undefined,
        theme_tertiary_color: configForm.theme_tertiary_color || undefined,
        theme_on_primary_color: configForm.theme_on_primary_color || undefined,
        theme_surface_accent_color: configForm.theme_surface_accent_color || undefined,
        logo_url: configForm.logo_url || undefined,
        favicon_url: configForm.favicon_url || undefined,
        notification_icon_url: configForm.notification_icon_url || undefined,
        description: configForm.description || undefined,
        is_active: configForm.is_active,
      });
      const wasNew = !configExists;
      setConfigExists(true);
      setSuccessMsg(wasNew ? 'Project configuration saved successfully' : 'Project configuration updated successfully');
      fetchConfiguredProjects();
      refreshProjectList();
      // Auto-switch to Budget Settings tab after saving
      setTimeout(() => { setSuccessMsg(null); setActiveTab('budgets'); }, 1500);
    } catch (err: any) {
      console.error('Failed to save config:', err);
      const status = err?.response?.status;
      const backendMsg = err?.response?.data?.message || err?.response?.data?.error;
      const detail = backendMsg || err?.message || 'Network error';
      setError(`Failed to save project configuration${status ? ` (${status})` : ''}: ${detail}`);
    } finally { setSaving(false); }
  };

  const handleSaveBudgetAccess = async () => {
    if (!editingProjectId) return;
    setSavingBudgetAccess(true); setError(null); setSuccessMsg(null);
    try {
      const budgetsPayload = budgets.map((b) => ({
        budget_id: b.budgetId,
        budget_name: b.budgetName,
        application_access: budgetAccessMap[b.budgetId] || 'none',
        is_store_level: budgetStoreLevelMap[b.budgetId] === true,
      }));
      await projectConfigAPI.saveBudgetAccess({ project_id: editingProjectId, budgets: budgetsPayload });
      setBudgetAccessExists(true);
      setSuccessMsg(budgetAccessExists ? 'Budget settings updated successfully' : 'Budget settings saved successfully');
      fetchConfiguredProjects();
      // Close modal after saving budget settings
      setTimeout(() => { setSuccessMsg(null); handleBackToList(); }, 1500);
    } catch (err: any) {
      console.error('Failed to save budget access:', err);
      setError(err.response?.data?.message || 'Failed to save budget settings');
    } finally { setSavingBudgetAccess(false); }
  };

  const isAccessEnabled = (access: string): boolean => access === 'web' || access === 'mobile' || access === 'both';

  const toggleBudgetAccess = (budgetId: number) => {
    const current = budgetAccessMap[budgetId] || 'none';
    const newAccess = isAccessEnabled(current) ? 'none' : 'both';
    setBudgetAccessMap((prev) => ({ ...prev, [budgetId]: newAccess }));
  };

  const toggleBudgetStoreLevel = (budgetId: number) => {
    setBudgetStoreLevelMap((prev) => ({ ...prev, [budgetId]: !prev[budgetId] }));
  };

  // ─── File upload handler ──────────────────────────────────────────────────
  const handleFileUpload = async (
    file: File,
    folder: string,
    field: 'logo_url' | 'favicon_url' | 'notification_icon_url',
    setUploading: (v: boolean) => void,
    setPreview: (v: string | null) => void,
  ) => {
    const label = field === 'logo_url' ? 'logo' : field === 'favicon_url' ? 'favicon' : 'notification icon';
    setUploading(true);
    setError(null);
    try {
      const oldPath = configForm[field];
      if (oldPath && !oldPath.startsWith('http')) {
        try { await uploadAPI.deleteFile(oldPath); } catch { /* ignore */ }
      }
      const res = await uploadAPI.uploadFile(file, folder);
      const blobPath = res.data?.data?.blobPath;
      const url = res.data?.data?.url;
      if (blobPath) {
        setConfigForm((f) => ({ ...f, [field]: blobPath }));
        setPreview(url || null);
      } else {
        setError(`Upload completed but ${label} path was not returned by the server.`);
      }
    } catch (err: any) {
      console.error(`${field} upload failed:`, err);
      const status = err?.response?.status;
      const backendMsg = err?.response?.data?.message || err?.response?.data?.error;
      const detail = backendMsg || err?.message || 'Network error';
      setError(`Failed to upload ${label}${status ? ` (${status})` : ''}: ${detail}`);
    } finally { setUploading(false); }
  };

  const handleFileRemove = async (
    field: 'logo_url' | 'favicon_url' | 'notification_icon_url',
    setPreview: (v: string | null) => void,
  ) => {
    const oldPath = configForm[field];
    setConfigForm((f) => ({ ...f, [field]: null }));
    setPreview(null);
    if (oldPath && !oldPath.startsWith('http')) {
      try { await uploadAPI.deleteFile(oldPath); } catch { /* ignore */ }
    }
  };

  // Helper to get project name from available projects
  const getProjectName = (projectId: number) => {
    const matched = availableProjects.find((p) => p.projectId === projectId);
    return matched?.projectName || null;
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  const formProjectName = configForm.project_name || getProjectName(editingProjectId!) || `Project #${editingProjectId}`;

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Ban className="mx-auto h-12 w-12 text-red-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-600">Access Denied</h3>
          <p className="mt-2 text-sm text-gray-400">Only Super Admins can manage projects.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ─── LIST VIEW (always rendered) ──────────────────────────────────────── */}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Management</h1>
            <p className="mt-1 text-sm text-gray-500">View and manage configured projects</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Project
            </button>
            {showAddDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => { setShowAddDropdown(false); setAddProjectSearch(''); }} />
                <div className="absolute right-0 top-full mt-2 z-20 w-80 rounded-xl border border-gray-100 bg-white shadow-xl overflow-hidden">
                  <div className="p-3 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={addProjectSearch}
                        onChange={(e) => setAddProjectSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredUnconfigured.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-400">
                        {unconfiguredProjects.length === 0 ? 'All projects are already configured' : 'No projects match your search'}
                      </div>
                    ) : (
                      filteredUnconfigured.map((project) => (
                        <button
                          key={project.projectId}
                          onClick={() => {
                            setShowAddDropdown(false);
                            setAddProjectSearch('');
                            openProjectForm(project.projectId);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-gray-50 last:border-0"
                        >
                          <div className="text-sm font-medium text-gray-900">{project.projectName}</div>
                          <div className="text-xs text-gray-400">ID: {project.projectId} &middot; Code: {project.projectCode}</div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Banners */}
        {view === 'list' && successMsg && <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"><CheckCircle className="h-4 w-4 flex-shrink-0" />{successMsg}</div>}
        {view === 'list' && error && <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"><AlertCircle className="h-4 w-4 flex-shrink-0" />{error}</div>}

        {/* Configured Projects Table */}
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
          {/* Search */}
          <div className="p-4 border-b border-gray-100">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search configured projects..."
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {listSearch && <button type="button" onClick={() => setListSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>}
            </div>
          </div>

          {listLoading ? (
            <BlockLoader className="py-16" />
          ) : filteredConfigured.length === 0 ? (
            <div className="py-16 text-center">
              <FolderOpen className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-600">
                {listSearch ? 'No projects match your search' : 'No Projects Configured'}
              </h3>
              <p className="mt-2 text-sm text-gray-400">
                {listSearch ? 'Try a different search term' : 'Click "Add Project" to configure your first project.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Project</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 w-40">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredConfigured.map((config, index) => (
                  <tr key={config.project_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-400">{index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {config.logo_url ? (
                          <img
                            src={resolveImageUrl(null, config.logo_url) || ''}
                            alt=""
                            className="h-8 w-8 rounded-lg border border-gray-200 object-contain bg-white p-0.5"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                            <FolderOpen className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{config.project_name || getProjectName(config.project_id) || 'Unnamed'}</div>
                          <div className="text-xs text-gray-400">ID: {config.project_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center px-4 py-3">
                      <TableActionGroup>
                        <TableActionButton action="edit" label="Edit" onClick={() => openProjectForm(config.project_id)} title="Edit" />
                        <TableActionButton action="delete" label="Delete" loading={deleting === config.project_id} onClick={() => handleDeleteProject(config.project_id)} disabled={deleting === config.project_id} title="Delete" />
                        <TableActionButton action={config.is_active ? 'deactivate' : 'activate'} label={config.is_active ? 'Deactivate' : 'Activate'} type="button" onClick={() => handleToggleActive(config)} title={config.is_active ? 'Deactivate' : 'Activate'} />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Summary */}
          {!listLoading && configuredProjects.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex items-center gap-4">
              <span>Total: <strong>{configuredProjects.length}</strong></span>
              <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-green-500" /> Active: <strong>{configuredProjects.filter((p) => p.is_active).length}</strong></span>
              <span className="flex items-center gap-1"><EyeOff className="h-3 w-3 text-gray-400" /> Inactive: <strong>{configuredProjects.filter((p) => !p.is_active).length}</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* ─── EDIT MODAL (standard project Modal) ─────────────────────────────── */}
      <Modal
        isOpen={view === 'form'}
        onClose={handleBackToList}
        title={formProjectName}
        size="xl"
        footer={
          <>
            <button onClick={handleBackToList} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            {activeTab === 'settings' && (
              <button onClick={handleSaveConfig} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving...' : configExists ? 'Update Project Settings' : 'Save Project Settings'}
              </button>
            )}
            {activeTab === 'budgets' && (
              <button onClick={handleSaveBudgetAccess} disabled={savingBudgetAccess} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50">
                {savingBudgetAccess ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {savingBudgetAccess ? 'Saving...' : budgetAccessExists ? 'Update Budget Settings' : 'Save Budget Settings'}
              </button>
            )}
          </>
        }
      >
        {/* Banners */}
        {successMsg && <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"><CheckCircle className="h-4 w-4 flex-shrink-0" />{successMsg}</div>}
        {error && <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"><AlertCircle className="h-4 w-4 flex-shrink-0" />{error}</div>}

        {loading ? (
          <BlockLoader className="py-20" />
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              <button onClick={() => setActiveTab('settings')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'settings' ? 'border-primary-500 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <Settings className="inline h-4 w-4 mr-1.5 -mt-0.5" /> Project Settings
              </button>
              <button
                onClick={() => configExists && setActiveTab('budgets')}
                disabled={!configExists}
                title={!configExists ? 'Save project settings first to configure budgets' : ''}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'budgets' ? 'border-primary-500 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'} ${!configExists ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FolderOpen className="inline h-4 w-4 mr-1.5 -mt-0.5" /> Budget Settings
              </button>
            </div>

            {/* ── Settings Tab ── */}
            {activeTab === 'settings' && (
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,280px)]">
                {/* Left column — form */}
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                    <input type="text" value={configForm.project_name || ''} onChange={(e) => setConfigForm((f) => ({ ...f, project_name: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500" placeholder="e.g. Apple India" />
                  </div>

                  {projectOwner && (projectOwner.fullName || projectOwner.employeeCode) && (
                    <div className="rounded-lg bg-primary-50 border border-primary-100 px-4 py-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Project Owner</label>
                      <p className="text-sm font-medium text-gray-900">
                        {projectOwner.fullName || 'Unknown'}{' '}
                        {projectOwner.employeeCode && <span className="text-gray-500">({projectOwner.employeeCode})</span>}
                      </p>
                    </div>
                  )}

                  {/* Brand assets — stacked rows for consistent layout regardless of upload state */}
                  <div className="space-y-2.5">
                    {[
                      {
                        label: 'Project Logo',
                        hint: 'PNG or SVG, square. Shown in web dashboard header and mobile home screen. Tip: export as a transparent PNG from your design tool — don’t paste a screenshot, the checkerboard would get baked into the image.',
                        previewUrl: resolveImageUrl(logoPreviewUrl, configForm.logo_url),
                        uploading: uploadingLogo,
                        accept: 'image/*',
                        onUpload: async (file: File) => handleFileUpload(file, PROJECT_LOGO_UPLOAD_FOLDER, 'logo_url', setUploadingLogo, setLogoPreviewUrl),
                        onRemove: () => handleFileRemove('logo_url', setLogoPreviewUrl),
                        darkBg: false,
                        transparencyCheck: true,
                      },
                      {
                        label: 'Favicon',
                        hint: '32×32 PNG or ICO. Used as the browser tab icon on the web app.',
                        previewUrl: resolveImageUrl(faviconPreviewUrl, configForm.favicon_url),
                        uploading: uploadingFavicon,
                        accept: 'image/*,.ico',
                        onUpload: async (file: File) => handleFileUpload(file, PROJECT_FAVICON_UPLOAD_FOLDER, 'favicon_url', setUploadingFavicon, setFaviconPreviewUrl),
                        onRemove: () => handleFileRemove('favicon_url', setFaviconPreviewUrl),
                        darkBg: false,
                        transparencyCheck: false,
                      },
                      {
                        label: 'Notification Icon',
                        hint: 'White on transparent PNG, used inside push notifications.',
                        previewUrl: resolveImageUrl(notificationIconPreviewUrl, configForm.notification_icon_url),
                        uploading: uploadingNotificationIcon,
                        accept: 'image/png',
                        onUpload: async (file: File) => handleFileUpload(file, PROJECT_NOTIFICATION_ICON_UPLOAD_FOLDER, 'notification_icon_url', setUploadingNotificationIcon, setNotificationIconPreviewUrl),
                        onRemove: () => handleFileRemove('notification_icon_url', setNotificationIconPreviewUrl),
                        darkBg: true,
                        transparencyCheck: false,
                      },
                    ].map((f) => (
                      <div key={f.label} className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-3">
                        {/* Fixed 72×72 slot — thumbnail or upload dropzone */}
                        <div className="flex-shrink-0">
                          {f.previewUrl ? (
                            <div
                              className={`flex h-[72px] w-[72px] items-center justify-center rounded-md border border-gray-200 p-1 ${
                                f.transparencyCheck ? '' : f.darkBg ? 'bg-gray-800' : 'bg-gray-50'
                              }`}
                              style={
                                f.transparencyCheck
                                  ? {
                                      // Transparency-checkerboard pattern. A truly transparent PNG
                                      // shows the checker through; a solid-bg PNG covers it with a
                                      // visible rectangle, making bad uploads obvious at a glance.
                                      backgroundColor: '#FFFFFF',
                                      backgroundImage:
                                        'linear-gradient(45deg, #E5E7EB 25%, transparent 25%), linear-gradient(-45deg, #E5E7EB 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #E5E7EB 75%), linear-gradient(-45deg, transparent 75%, #E5E7EB 75%)',
                                      backgroundSize: '12px 12px',
                                      backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                                    }
                                  : undefined
                              }
                            >
                              <img src={f.previewUrl} alt={f.label} className={`max-h-full max-w-full object-contain ${f.darkBg ? 'brightness-0 invert' : ''}`} onError={(e) => { (e.target as HTMLImageElement).src = ''; }} />
                            </div>
                          ) : (
                            <label className="flex h-[72px] w-[72px] cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 transition-colors hover:border-primary-400 hover:bg-primary-50">
                              {f.uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                              <span className="text-[10px]">{f.uploading ? 'Uploading' : 'Upload'}</span>
                              <input type="file" accept={f.accept} className="hidden" disabled={f.uploading} onChange={async (e) => { const file = e.target.files?.[0]; if (file) { await f.onUpload(file); } e.target.value = ''; }} />
                            </label>
                          )}
                        </div>

                        {/* Right side — label, hint, actions */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <ImageIcon className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-900">{f.label}</span>
                          </div>
                          <p className="mt-0.5 text-xs text-gray-500">{f.hint}</p>
                          {f.previewUrl && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                <Pencil className="h-3 w-3" /> Replace
                                <input type="file" accept={f.accept} className="hidden" disabled={f.uploading} onChange={async (e) => { const file = e.target.files?.[0]; if (file) { await f.onUpload(file); } e.target.value = ''; }} />
                              </label>
                              <button type="button" onClick={f.onRemove} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50">
                                <Trash2 className="h-3 w-3" /> Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Theme Editor (5 color pickers + contrast warnings) */}
                  <ProjectThemeEditor
                    value={{
                      primary: configForm.theme_primary_color,
                      secondary: configForm.theme_secondary_color,
                      tertiary: configForm.theme_tertiary_color,
                      onPrimary: configForm.theme_on_primary_color,
                      surfaceAccent: configForm.theme_surface_accent_color,
                    }}
                    onChange={(next) => setConfigForm((f) => ({
                      ...f,
                      theme_primary_color: next.primary,
                      theme_secondary_color: next.secondary,
                      theme_tertiary_color: next.tertiary,
                      theme_on_primary_color: next.onPrimary,
                      theme_surface_accent_color: next.surfaceAccent,
                    }))}
                  />

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={configForm.description || ''} onChange={(e) => setConfigForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500" placeholder="Brief description of this project..." />
                  </div>

                </div>

                {/* Right column — live mobile preview */}
                <div>
                  <ProjectMobilePreview
                    theme={{
                      primary: configForm.theme_primary_color,
                      secondary: configForm.theme_secondary_color,
                      tertiary: configForm.theme_tertiary_color,
                      onPrimary: configForm.theme_on_primary_color,
                      surfaceAccent: configForm.theme_surface_accent_color,
                    }}
                    logoUrl={resolveImageUrl(logoPreviewUrl, configForm.logo_url)}
                  />
                </div>
              </div>
            )}

            {/* ── Budgets Tab ── */}
            {activeTab === 'budgets' && (
              <div className="space-y-5">
                {/* The project's Hiring switch. Here from the day Hiring is deployed, with
                    no server setting added; it hides itself only while Hiring has been
                    stopped on the server (the emergency off), and this tab is then
                    exactly as it was. */}
                {editingProjectId != null && <HiringSeeSwitchCard projectId={editingProjectId} />}
                {budgets.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-10 text-center">
                    <FolderOpen className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-3 text-sm text-gray-500">No active budgets found for this project.</p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="text" placeholder="Search budgets..." value={budgetAccessSearch} onChange={(e) => setBudgetAccessSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                      {budgetAccessSearch && <button type="button" onClick={() => setBudgetAccessSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>}
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Enabled: <strong>{budgets.filter((b) => isAccessEnabled(budgetAccessMap[b.budgetId] || 'none')).length}</strong></span>
                      <span className="flex items-center gap-1"><Ban className="h-3.5 w-3.5 text-red-400" /> Disabled: <strong>{budgets.filter((b) => !isAccessEnabled(budgetAccessMap[b.budgetId] || 'none')).length}</strong></span>
                    </div>

                    <div className="rounded-lg border border-gray-100 overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50/70 border-b border-gray-200">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">#</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Budget Name</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 w-28">Access</th>
                            {/* Store-level = a store-deployable field role. Only these
                                appear in Dashboard → Onboarding's new-joiner list. */}
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 w-32" title="Store-deployable field roles (iPro / Senior iPro). Only these show in the Onboarding new-joiner worklist.">Store-level</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredBudgetSettings.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">No budgets match your search</td></tr>
                          ) : (
                            filteredBudgetSettings.map((budget, index) => {
                              const enabled = isAccessEnabled(budgetAccessMap[budget.budgetId] || 'none');
                              const storeLevel = budgetStoreLevelMap[budget.budgetId] === true;
                              return (
                                <tr key={budget.budgetId} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 text-sm text-gray-400">{index + 1}</td>
                                  <td className="px-4 py-3"><span className="text-sm font-medium text-gray-900">{budget.budgetName}</span></td>
                                  <td className="px-4 py-3 text-center">
                                    <button type="button" onClick={() => toggleBudgetAccess(budget.budgetId)} className="inline-flex items-center gap-1.5 text-sm font-medium">
                                      {enabled ? <ToggleRight className="h-6 w-6 text-green-500" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
                                      <span className={enabled ? 'text-green-700' : 'text-gray-400'}>{enabled ? 'Enabled' : 'Disabled'}</span>
                                    </button>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <button type="button" onClick={() => toggleBudgetStoreLevel(budget.budgetId)} className="inline-flex items-center gap-1.5 text-sm font-medium" title="Mark this budget as a store-deployable field role">
                                      {storeLevel ? <ToggleRight className="h-6 w-6 text-blue-500" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
                                      <span className={storeLevel ? 'text-blue-700' : 'text-gray-400'}>{storeLevel ? 'Store' : '—'}</span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
